<script lang="ts">
  /**
   * SettingsOverlay - Modal overlay for game settings
   * Provides volume control and sound toggle
   */

  import { settings } from '$lib/stores/settings';

  // Props
  interface Props {
    visible?: boolean;
    onClose?: () => void;
  }

  let {
    visible = false,
    onClose = () => {}
  }: Props = $props();

  // Handle backdrop click
  function handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  // Handle volume change
  function handleVolumeChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const volume = parseFloat(target.value);
    settings.setVolume(volume);
  }

  // Handle sound toggle
  function handleSoundToggle() {
    settings.toggleSound();
  }
</script>

{#if visible}
  <div
    class="overlay-backdrop"
    onclick={handleBackdropClick}
    role="dialog"
    aria-modal="true"
    aria-labelledby="settings-title"
  >
    <div class="settings-panel">
      <h2 id="settings-title" class="title">Settings</h2>

      <div class="settings-content">
        <!-- Volume Control -->
        <div class="setting-item">
          <label for="volume-slider" class="setting-label">
            Volume
            <span class="volume-value">{Math.round($settings.volume * 100)}%</span>
          </label>
          <input
            id="volume-slider"
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={$settings.volume}
            onchange={handleVolumeChange}
            class="volume-slider"
            aria-label="Volume control"
          />
        </div>

        <!-- Sound Toggle -->
        <div class="setting-item">
          <label class="setting-label">Sound</label>
          <button
            class="toggle-button"
            class:active={$settings.soundEnabled}
            onclick={handleSoundToggle}
            aria-label="Toggle sound"
            aria-pressed={$settings.soundEnabled}
          >
            <span class="toggle-text">
              {$settings.soundEnabled ? 'ON' : 'OFF'}
            </span>
            <span class="toggle-icon">
              {$settings.soundEnabled ? '🔊' : '🔇'}
            </span>
          </button>
        </div>
      </div>

      <!-- Close Button -->
      <button class="close-button" onclick={onClose} aria-label="Close settings">
        ✕ Close
      </button>
    </div>
  </div>
{/if}

<style>
  .overlay-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .settings-panel {
    background: var(--color-space-blue);
    border-radius: var(--radius-lg);
    padding: var(--space-xl);
    width: 90%;
    max-width: 400px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    animation: slideUp 0.3s ease;
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .title {
    font-size: var(--font-heading);
    font-weight: bold;
    color: var(--color-bright-yellow);
    margin: 0 0 var(--space-lg) 0;
    text-align: center;
  }

  .settings-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
    margin-bottom: var(--space-xl);
  }

  .setting-item {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
  }

  .setting-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-lg);
    color: var(--color-text-primary);
    font-weight: 600;
  }

  .volume-value {
    font-size: var(--font-body);
    color: var(--color-cosmic-purple);
  }

  /* Volume Slider */
  .volume-slider {
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      var(--color-cosmic-purple),
      var(--color-bright-yellow)
    );
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
  }

  .volume-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease;
  }

  .volume-slider::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  .volume-slider::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease;
  }

  .volume-slider::-moz-range-thumb:hover {
    transform: scale(1.2);
  }

  /* Toggle Button */
  .toggle-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-md);
    border: 2px solid var(--color-text-muted);
    border-radius: var(--radius-md);
    background: var(--color-space-deep);
    color: var(--color-text-secondary);
    font-size: var(--font-lg);
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .toggle-button.active {
    border-color: var(--color-success);
    background: rgba(16, 185, 129, 0.2);
    color: var(--color-success);
  }

  .toggle-button:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .toggle-text {
    font-weight: bold;
  }

  .toggle-icon {
    font-size: 24px;
  }

  /* Close Button */
  .close-button {
    width: 100%;
    padding: var(--space-md);
    border: none;
    border-radius: var(--radius-md);
    background: var(--color-cosmic-purple);
    color: white;
    font-size: var(--font-lg);
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-button:hover {
    background: var(--color-bright-yellow);
    color: var(--color-space-deep);
    transform: scale(1.05);
  }

  .close-button:active {
    transform: scale(0.98);
  }

  /* Responsive Design */
  @media (max-width: 767px) {
    .settings-panel {
      width: 95%;
      padding: var(--space-lg);
    }

    .title {
      font-size: 24px;
    }
  }
</style>
